"use client";

import { Share2, Check, MessageCircle, Link as LinkIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useHasMounted } from "@/hooks/use-has-mounted";
import { ShareDialog, type ShareOption } from "./share-dialog";
import { getShareTargets } from "./share-targets";
import { useShareDialog } from "./use-share-dialog";

interface ShareButtonProps {
 title: string;
 url?: string;
 showTextOnMobile?: boolean;
}

function useShareOptions(title: string, dialog: ReturnType<typeof useShareDialog>): ShareOption[] {
 const hasMounted = useHasMounted();
 const hasNativeShare = hasMounted && "share" in navigator;
 const { copiedLink } = dialog;
 const copyOption: ShareOption = {
  name: copiedLink ? "Link copied!" : "Copy Link",
  icon: copiedLink ? (
   <Check className="size-5 text-success-strong" />
  ) : (
   <LinkIcon className="size-5" />
  ),
  action: dialog.copyToClipboard,
 };
 const targetOptions = getShareTargets(title, dialog.shareUrl).map((target) => ({
  name: target.name,
  icon: target.icon,
  action: () => {
   target.open();
   dialog.close();
  },
 }));
 const baseShareOptions = [copyOption, ...targetOptions];
 return hasNativeShare
  ? [
    ...baseShareOptions,
    {
     name: "More options",
     icon: <MessageCircle className="size-5" />,
     action: dialog.shareViaNative,
    },
   ]
  : baseShareOptions;
}

export function ShareButton({
 title,
 url,
 showTextOnMobile = true,
}: ShareButtonProps) {
 const dialog = useShareDialog(title, url);
 const shareOptions = useShareOptions(title, dialog);

 return (
  <>
   <Button
    variant="ghost"
    size="sm"
    onClick={dialog.open}
    className={cn(
     "inline-flex items-center gap-2 rounded-lg border border-border text-foreground hover:border-border hover:bg-muted",
     showTextOnMobile ? "px-4 py-2" : "p-2 max-md:size-11 max-md:border-0 max-md:p-0",
    )}
    aria-label="Share listing"
   >
    <Share2 className="size-4" />
    {showTextOnMobile && (
     <span className="text-sm font-semibold underline">Share</span>
    )}
   </Button>

   {dialog.isOpen && (
    <ShareDialog
     shareUrl={dialog.shareUrl}
     options={shareOptions}
     copiedLink={dialog.copiedLink}
     onCopy={dialog.copyToClipboard}
     onClose={dialog.close}
    />
   )}
  </>
 );
}
